import React from 'react'
import { connect } from 'react-redux'

import moment from 'moment'
import { TidewaysContext } from '../app/context'
import { isAggregatedTrace, isCallgraph, TraceSummary } from '../domain/TraceSummary'
import Bottlenecks from '../helper/bottlenecks'
import LargeNumber from '../helper/large-number'
import Link from '../helper/link'
import Memory from '../helper/memory'
import Milliseconds from '../helper/milliseconds'
import Percent from '../helper/percent'
import Shorten from '../helper/shorten'
import MomentDate from '../momentDate'
import { ApplicationState } from '../redux/ApplicationState'

interface OwnProps {
    profile: TraceSummary
    rowType: string
    route: string
    link?: string
    compact: boolean
}

interface StateProps {
    tidewaysContext: TidewaysContext
}

type Props = OwnProps & StateProps

class Profile extends React.Component<Props> {
    public static defaultProps = {
        route: 'XhprofProfilerBundle.Profile.quick',
        rowType: '',
        compact: false,
    }

    public render() {
        const { profile } = this.props

        let statusCode = ''
        if (profile.statusCode) {
            if (profile.statusCode < 300) {
                statusCode = 'success'
            } else if (profile.statusCode < 400) {
                statusCode = 'warning'
            } else {
                statusCode = 'danger'
            }
        }

        return (
            <tbody>
                <tr className={this.props.rowType + ' summary'}>
                    <td colSpan={1} className="number short" style={{ minWidth: '80px' }}>
                        <Milliseconds value={profile.wallTime} />
                    </td>
                    <td colSpan={8} className="operation">
                        <Link
                            className="profile-list-item"
                            route={this.props.route}
                            params={{
                                profile: profile.id,
                                s: profile.service,
                                env: profile.environment,
                                measured: moment.utc(profile.measureDate.date).unix(),
                            }}
                        >
                            <strong>
                                <Bottlenecks bottlenecks={profile.bottlenecks} bottleneckTypes={profile.bottleneckTypes} />
                                <Shorten
                                    string={(profile.name ? profile.name + ' | ' : '') + profile.operationIdentifier || 'Trace Details'}
                                />
                                {profile.operation !== profile.operationIdentifier ? (
                                    <small className={'text-muted'}> {profile.operation}</small>
                                ) : null}
                                {profile.transactionTags.map(tag => (
                                    <span
                                        key={tag}
                                        className="tw-ml-8 label label-outline-default tw-tooltip tw-tooltip-dataText tw-tooltip--top"
                                        data-tooltip-text={`This trace is tagged as ${tag}.`}
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </strong>
                        </Link>
                        {profile.errorType && (
                            <span>
                                {' '}
                                <small>
                                    {profile.isIgnoredErrorType ? (
                                        <span
                                            className="text-muted tw-tooltip tw-tooltip-dataText tw-tooltip--bottom"
                                            data-tooltip-text="This exception class is classified as ignored in this project, probably because its used for control-flow instead of actually being an error."
                                        >
                                            {profile.errorType}
                                        </span>
                                    ) : (
                                        <span className="text-danger">{profile.errorType}</span>
                                    )}
                                </small>
                            </span>
                        )}
                    </td>
                    <td className="number width-22p">
                        <MomentDate date={profile.measureDate.date} />
                    </td>
                </tr>
                {profile.title.length > 0 ? (
                    <tr className={this.props.rowType + ' details'}>
                        <td colSpan={1} className="number short" style={{ minWidth: '80px' }}>
                            <Memory value={profile.memory * 1024} />
                        </td>
                        <td colSpan={7} className="title word">
                            <div className="profile-list-item-metadata">
                                {statusCode && (
                                    <span>
                                        <span className={'label label-' + statusCode}>{profile.statusCode}</span>{' '}
                                    </span>
                                )}
                                <small>
                                    <Shorten string={profile.title} length={250} />
                                </small>
                            </div>
                        </td>
                        <td
                            className="number word text-ellipsis"
                            style={{ overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '100px' }}
                            title={profile.server ? profile.server : 'unknown server'}
                        >
                            <small>{profile.server ? profile.server : 'unknown server'}</small>
                        </td>
                        <td
                            className="number word"
                            style={{ overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '100px' }}
                            title={profile.service + ' @ ' + profile.environment}
                        >
                            <small>
                                {profile.service} @ {profile.environment}
                            </small>
                        </td>
                    </tr>
                ) : null}
                {this.props.compact ? null : (
                    <tr className={this.props.rowType + ' details layers'}>
                        <td className="number short">
                            {isCallgraph(profile) ? <i className="fa fa-sitemap tw-color-grey-600" title="callgraph trace" /> : null}
                            {isAggregatedTrace(this.props.profile) ? (
                                <i
                                    className="tw-ml-4 fa fa-tasks tw-color-grey-600"
                                    title="Aggregated Trace: averaged representation of multiple Traces"
                                />
                            ) : null}
                        </td>
                        <td className="number">
                            {profile.layerReport.db ? (
                                <span>
                                    <small>SQL ({<LargeNumber value={profile.layerReport.db.ct} class="default" />}):</small>{' '}
                                    <Milliseconds value={profile.layerReport.db.wt} class="layer-sql" />
                                </span>
                            ) : null}
                        </td>
                        <td className="number">
                            {profile.layerReport.http ? (
                                <span>
                                    <small>HTTP ({<LargeNumber value={profile.layerReport.http.ct} class="default" />}):</small>{' '}
                                    <Milliseconds value={profile.layerReport.http.wt} class="layer-http" />
                                </span>
                            ) : null}
                        </td>
                        <td className="number">
                            {profile.layerReport.cache ? (
                                <span>
                                    <small>
                                        Memcache ({<LargeNumber value={profile.layerReport.cache.ct} class="default" />}
                                        ):
                                    </small>{' '}
                                    <Milliseconds value={profile.layerReport.cache.wt} class="layer-cache" />
                                </span>
                            ) : null}
                        </td>
                        <td className="number">
                            {profile.layerReport.apcu ? (
                                <span>
                                    <small>APCu</small> <Milliseconds value={profile.layerReport.apcu.wt} class="layer-cache" />
                                </span>
                            ) : null}
                        </td>
                        <td className="number">
                            {profile.layerReport.redis ? (
                                <span>
                                    <small>Redis ({<LargeNumber value={profile.layerReport.redis.ct} class="default" />}):</small>{' '}
                                    <Milliseconds value={profile.layerReport.redis.wt} class="layer-redis" />
                                </span>
                            ) : null}
                        </td>
                        <td className="number">
                            {profile.layerReport.mongo ? (
                                <span>
                                    <small>MongoDB ({<LargeNumber value={profile.layerReport.mongo.ct} class="default" />}):</small>{' '}
                                    <Milliseconds value={profile.layerReport.mongo.wt} class="layer-mongo" />
                                </span>
                            ) : null}
                        </td>
                        <td className="number">
                            {profile.layerReport.al ? (
                                <span>
                                    <small>Autoloading</small> <Milliseconds value={profile.layerReport.al.wt} class="layer-io" />
                                </span>
                            ) : null}
                        </td>
                        <td className="number">
                            {profile.layerReport.fio ? (
                                <span>
                                    <small>File I/O</small> <Milliseconds value={profile.layerReport.fio.wt} class="layer-io" />
                                </span>
                            ) : null}
                        </td>
                        <td className="number">
                            {profile.layerReport.runq ? (
                                <span>
                                    <small>Waiting for CPU</small> <Milliseconds value={profile.layerReport.runq.wt} class="layer-runq" />
                                </span>
                            ) : null}

                            {!profile.layerReport.runq && profile.layerReport.uio ? (
                                <span>
                                    <small>Unaccounted Wait</small> <Milliseconds value={profile.layerReport.uio.wt} class="layer-io" />
                                </span>
                            ) : profile.layerReport.io && profile.layerReport.io.perc ? (
                                <span>
                                    <small>
                                        Non-CPU time (<Percent value={profile.layerReport.io.perc / 100} />
                                        ):
                                    </small>
                                    <Milliseconds value={profile.layerReport.io.wt} class="layer-io" />
                                </span>
                            ) : null}
                        </td>
                    </tr>
                )}
            </tbody>
        )
    }
}

export default connect((globalState: ApplicationState, props) => {
    return {
        ...props,
        tidewaysContext: globalState.tidewaysContext,
    }
})(Profile)
